import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Index, Colors } from '@client/style/Variables';
import gsap from 'gsap';
import Site from '@client/store/Site';
import { Splash } from './Splash';
import MenuLinks from './MenuLinks';
import { TextStyle } from '@client/style/TextStyles';
import LogoWithText from './LogoWithText';

const FullScreenMenuWrapper = styled.nav`
	display: none;
	position: fixed;
	width: 100vw;
	height: 100vh;
	top: 0;
	left: 0;
	z-index: ${Index.FullScreenMenu};
	overflow: hidden;
	pointer-events: none;
`;

const StyledMenuInner = styled.div`
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	height: 100vh;
	color: ${Colors.lightText};
	background-color: inherit;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	pointer-events: all;
`;

const duration = 0.4;
const delay = 0.2;
const stagger = 0.04;

export default function MenuContainer({ isOpen }: { isOpen: boolean }) {
	const topMenuState = Site.use(state => state.topMenu);

	const itemsOpacityTween = useRef<gsap.core.Tween>(undefined);
	const itemsPositionTween = useRef<gsap.core.Tween>(undefined);
	const containerDisplayTween = useRef<gsap.core.Tween>(undefined);
	const fullScreenMenu = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const items = fullScreenMenu.current?.querySelectorAll('.menu-item') ?? null;

		itemsOpacityTween.current = gsap.to(items, {
			stagger: {
				each: stagger,
				ease: 'power1.out',
			},
			delay: isOpen ? delay : 0,
			duration: duration,
			ease: isOpen ? 'back.out(2)' : 'back.in(1)',
			x: isOpen ? 0 : '20%',
			onComplete: () => {
				if (!isOpen) {
					gsap.set(items, {
						x: '-20%',
					});
				}
			},
		});
		itemsPositionTween.current = gsap.to(items, {
			stagger: {
				each: stagger,
				ease: 'power1.out',
			},
			delay: isOpen ? delay : 0,
			duration: duration,
			ease: 'linear',
			opacity: isOpen ? 1 : 0,
		});

		containerDisplayTween.current = gsap.to(fullScreenMenu.current, {
			delay: isOpen ? 0 : 0.8,
			display: isOpen ? 'block' : 'none',
		});

		return () => {
			itemsOpacityTween.current?.kill();
			itemsPositionTween.current?.kill();
			containerDisplayTween.current?.kill();
		};
	}, [isOpen]);

	return (
		<FullScreenMenuWrapper ref={fullScreenMenu}>
			<Splash fill={isOpen} color={topMenuState.backgroundColor?.color} />
			<StyledMenuInner>
				<MenuLinks />
				<LogoWithText className={'menu-item'} />
			</StyledMenuInner>
		</FullScreenMenuWrapper>
	);
}
